import {
  createExpoFileSystemAdapter,
  useFileSystemDevTools,
} from "@rozenite/file-system-plugin";
import { useRequireProfilerDevTools } from "@rozenite/require-profiler-plugin";
import {
  createExpoSecureStorageAdapter,
  createMMKVStorageAdapter,
  useRozeniteStoragePlugin,
} from "@rozenite/storage-plugin";
import { useTanStackQueryDevTools } from "@rozenite/tanstack-query-plugin";
import { merge } from "es-toolkit/compat";
import * as FileSystem from "expo-file-system";
import { DarkTheme, ThemeProvider } from "expo-router/react-navigation";
import * as SecureStore from "expo-secure-store";
import * as SplashScreen from "expo-splash-screen";
import type { PropsWithChildren } from "react";
import React, { useLayoutEffect, useState } from "react";
import { Platform, View } from "react-native";
import { SystemBars } from "react-native-edge-to-edge";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";
import { useAssetsViewer } from "rozenite-assets-viewer";

import PortalBoundary from "$src/core/components/atoms/PortalBoundary";
import Toaster from "$src/core/components/molecules/Toaster/Toaster";
import CarIntegration from "$src/core/components/organisms/CarIntegration/CarIntegration";
import AnalyticsProvider from "$src/core/contexts/analytics";
import ApiProvider from "$src/core/contexts/api";
import AuthProvider from "$src/core/contexts/auth";
import ConfigProvider from "$src/core/contexts/config";
import QueryProvider, { queryClient } from "$src/core/contexts/query";
import TranslationProvider from "$src/core/contexts/translation";
import TvConnectHostProvider from "$src/core/contexts/tv-connect-host";
import TvConnectRemoteProvider from "$src/core/contexts/tv-connect-remote";
import useAuth from "$src/core/hooks/use-auth";
import useOnlineStatus from "$src/core/hooks/use-online-status";
import useTranslation from "$src/core/hooks/use-translation";
import type { Config } from "$src/core/models/config";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import useMenuQuery from "$src/core/queries/use-menu-query";
import useThemeQuery from "$src/core/queries/use-theme-query";
import { isCarIntegrationSupported } from "$src/core/services/car/car";
import Logging from "$src/core/services/logging/logging";
import { SecureStorageItemTypes } from "$src/core/services/secure-storage/constants";
import { storage } from "$src/core/services/storage/storage";
import opacity from "$src/core/utils/color/opacity";
import isUnsupportedTvDevice from "$src/core/utils/device/is-unsupported-tv-device";
import CustomPlatform from "$src/core/utils/platform/platform";

const secureStorageKeys = Object.keys(SecureStorageItemTypes) as Array<
  keyof typeof SecureStorageItemTypes
>;

const RozeniteDevtools = ({ slug }: { slug: string }) => {
  useTanStackQueryDevTools(queryClient);
  useRequireProfilerDevTools();

  useAssetsViewer({
    folderPaths: [`src/flavors/${slug}/assets`, "src/core/assets"],
  });

  useFileSystemDevTools({
    adapter: createExpoFileSystemAdapter(FileSystem),
  });

  useRozeniteStoragePlugin({
    storages: [
      createMMKVStorageAdapter({
        storages: { storage },
      }),
      createExpoSecureStorageAdapter({
        storage: SecureStore,
        keys: secureStorageKeys,
      }),
    ],
  });

  return null;
};

type Props = {
  config: Config;
};

const stylesheet = StyleSheet.create(() => ({
  gestureHandlerRootView: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
}));

const THEME = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: "transparent",
  },
};

const SPLASH_HIDE_RETRY_MS = 250;
const SPLASH_HIDE_RETRIES = 8;

// Only iOS and tvOS `hide()` can stop early. Android sets a sticky flag, and on
// web `hide()` is empty, so more calls do nothing on those platforms.
const SPLASH_HIDE_NEEDS_RETRY = CustomPlatform.select({
  ios: true,
  tvOS: true,
  default: false,
});

const Children = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { status: authStatus } = useAuth();
  const { isOnline, recheck } = useOnlineStatus();

  const {
    error: menuError,
    refetch: refetchMenu,
    isLoading: isMenuLoading,
  } = useMenuQuery();
  const {
    data: theme,
    error: themeError,
    refetch: refetchTheme,
    isLoading: isThemeLoading,
  } = useThemeQuery();

  const [isReady, setIsReady] = useState(false);

  const hasBlockingError = isOnline === false || !!menuError || !!themeError;

  /* Hide splash on layout-ready or terminal error, else it stays up forever. */
  useLayoutEffect(() => {
    if (!isReady && !hasBlockingError) {
      return undefined;
    }

    // We do not know the root cause. iOS `hide()` stops early if loadingView
    // is nil or hidden, and it does not retry.
    SplashScreen.hide();

    if (!SPLASH_HIDE_NEEDS_RETRY) {
      return undefined;
    }

    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts += 1;
      SplashScreen.hide();
      if (attempts >= SPLASH_HIDE_RETRIES) {
        clearInterval(intervalId);
      }
    }, SPLASH_HIDE_RETRY_MS);

    return () => clearInterval(intervalId);
  }, [isReady, hasBlockingError]);

  useLayoutEffect(() => {
    if (theme) {
      UnistylesRuntime.updateTheme("brand", (currentTheme) =>
        merge(currentTheme, {
          colors: {
            backgroundDefault: theme.colors?.backgroundDefault,
            backgroundBrand: theme.colors?.backgroundBrand,
            backgroundSurface: theme.colors?.backgroundSurface,
            backgroundFloat: theme.colors?.backgroundFloat,
            backgroundInverted: theme.colors?.backgroundInverted,

            backgroundActive: theme.colors?.backgroundBrand
              ? opacity(theme.colors.backgroundBrand, 0.9)
              : undefined,
            backgroundFocus: theme.colors?.backgroundInverted,
            backgroundHover:
              (theme.colors?.backgroundSurface
                ? opacity(theme.colors.backgroundSurface, 0.8)
                : undefined) || theme.colors?.backgroundFloat,

            foregroundOnDefault: theme.colors?.foregroundDefault,
            foregroundDefaultMuted: theme.colors?.foregroundDefault
              ? opacity(theme.colors.foregroundDefault, 0.5)
              : undefined,
            foregroundOnActive: theme.colors?.foregroundBrand,
            foregroundOnFocus: theme.colors?.foregroundInverted,
            foregroundOnHover: theme.colors?.foregroundDefault,
            foregroundOnFloat: theme.colors?.foregroundDefault,
            foregroundOnBrand: theme.colors?.foregroundBrand,
            foregroundOnInverted: theme.colors?.foregroundInverted,
            foregroundOnSuccess: theme.colors?.foregroundDefault,
            foregroundOnError: theme.colors?.foregroundDefault,

            borderDefault: theme.colors?.foregroundDefault
              ? opacity(theme.colors.foregroundDefault, 0.1)
              : undefined,
            borderFocus: theme.colors?.foregroundDefault,
          },
        }),
      );
    }
  }, [theme]);

  /* Make sure TV app isn't being used on a non-TV */
  useLayoutEffect(() => {
    if (isUnsupportedTvDevice()) {
      SplashScreen.hideAsync().catch(Logging.error);
    }
  }, []);

  if (isUnsupportedTvDevice()) {
    return (
      <ErrorPage
        title={t("ERROR/TV_APP/UNSUPPORTED_DEVICE/TITLE")}
        subtitle={t("ERROR/TV_APP/UNSUPPORTED_DEVICE/SUBTITLE")}
      />
    );
  }

  if (isOnline === false) {
    return (
      <ErrorPage
        title={t("ERROR/NO_CONNECTION/TITLE")}
        subtitle={t("ERROR/NO_CONNECTION/SUBTITLE")}
        ctaIcon="retry"
        ctaText={t("COMMON/RETRY")}
        onPressCta={recheck}
      />
    );
  }

  if (menuError) {
    return (
      <ErrorPage
        title={t("ERROR/GENERIC/TITLE")}
        subtitle={t("ERROR/GENERIC/SUBTITLE")}
        ctaIcon="retry"
        ctaText={t("COMMON/RETRY")}
        onPressCta={refetchMenu}
        error={menuError}
      />
    );
  }

  if (themeError) {
    return (
      <ErrorPage
        title={t("ERROR/GENERIC/TITLE")}
        subtitle={t("ERROR/GENERIC/SUBTITLE")}
        ctaIcon="retry"
        ctaText={t("COMMON/RETRY")}
        onPressCta={refetchTheme}
        error={themeError}
      />
    );
  }

  if (!authStatus || isMenuLoading || isThemeLoading) return null;

  return (
    <View style={stylesheet.container} onLayout={() => setIsReady(true)}>
      <SystemBars style="light" />
      {children}
    </View>
  );
};

const Core = ({ config, children }: PropsWithChildren<Props>) => {
  useLayoutEffect(() => {
    if (config.logging) {
      Logging.init?.(config.logging);
    }
  }, [config.logging]);

  return (
    <ThemeProvider value={THEME}>
      <GestureHandlerRootView style={stylesheet.gestureHandlerRootView}>
        <SafeAreaProvider>
          <PortalBoundary>
            <QueryProvider>
              {__DEV__ && Platform.OS !== "web" ? (
                <RozeniteDevtools slug={config.slug} />
              ) : null}
              <ConfigProvider config={config}>
                <AnalyticsProvider>
                  <ApiProvider api={config.api}>
                    <AuthProvider>
                      <TranslationProvider>
                        {/* Outside <Children>: a cold in-car launch never
                            renders the gated UI tree. */}
                        {isCarIntegrationSupported && <CarIntegration />}
                        <TvConnectRemoteProvider>
                          <TvConnectHostProvider>
                            <Children>{children}</Children>
                          </TvConnectHostProvider>
                        </TvConnectRemoteProvider>
                      </TranslationProvider>
                    </AuthProvider>
                  </ApiProvider>
                </AnalyticsProvider>
              </ConfigProvider>
            </QueryProvider>
          </PortalBoundary>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </ThemeProvider>
  );
};

const maybeWrapLogging = (Component: React.ComponentType) => {
  if (Logging.wrap) {
    return Logging.wrap(Component);
  }
  return Component;
};

export const withCore = (config: Config, Component: React.ComponentType) =>
  maybeWrapLogging(() => (
    <Core config={config}>
      <Component />

      <Toaster />
    </Core>
  ));
